import useUIStore from '@/modules/layout/store/ui.store';
import useAuthStore from '@/modules/auth/store/auth.store';
import type { Router } from 'vue-router';
import { includes, some } from 'lodash';

export function appendAuthInterceptor(router: Router): void {
    router.beforeEach(async (to, from, next) => {
        // Always persist the intended destination so the app can redirect back
        // after a successful login (e.g. deep-link handling).
        const uiStore = useUIStore();
        uiStore.setNextUrl(to.fullPath);

        // Routes without `meta.requiresAuth` are public —
        // skip all auth/role checks and let the navigation proceed.
        const isRequiresAuth = !!to.meta.requiresAuth;
        if (!isRequiresAuth) {
            return next();
        }

        const authStore = useAuthStore();
        const isAuthenticated: boolean = authStore.selectIsAuthenticated;

        // Authentication check: unauthenticated users are redirected to Home,
        // where the MSALLogin component triggers the MSAL redirect flow.
        // The to.name === 'Home' guard is required to break an infinite redirect
        // loop: Home has requiresAuth:true, so without this check the interceptor
        // would redirect Home → Home → Home until the browser locks up.
        // Letting the navigation to Home proceed allows MSAL to take over.
        if (!isAuthenticated) {
            if (to.name === 'Home') {
                return next();
            }
            sessionStorage.setItem('postLoginRedirect', to.fullPath);
            return next({ name: 'Home' });
        }

        // Authorization check: compare the user's access-token roles against the
        // roles declared on the route's meta. Falling back to [] guards against
        // undefined when the store is not yet populated (e.g. on first load before
        // the MSAL token exchange completes).
        // Routes that declare no roles array are treated as accessible to any
        // authenticated user (!allowedRoles.length).
        const allowedRoles: string[] = (to.meta.roles as string[]) ?? [];
        const userRoles: string[] = authStore.selectAccessTokenRoles ?? [];
        const hasAccess: boolean = !allowedRoles.length || some(userRoles, (role) => includes(allowedRoles, role));

        // Insufficient roles: redirect to Home rather than next(false) so the
        // user lands on a valid page rather than a blank one.
        // The to.name === 'Home' guard prevents the same redirect loop described
        // above — roles may be empty during the MSAL token exchange on Home,
        // so we let it through rather than immediately bouncing back.
        if (!hasAccess) {
            if (to.name === 'Home') {
                return next();
            }
            return next({ name: 'Home' });
        }

        return next();
    });
}
